import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Compass, Video, Users, MessageCircle, Globe, User, Settings, Shield, LogOut, Coins, X, Menu } from "lucide-react";
import { YaprioLogo } from "@/components/YaprioLogo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { refreshMessageUnread, subscribeMessageUnread } from "@/lib/messageUnread";
import { useLanguage } from "@/context/LanguageContext";

const NAV = [
  { to: "/home", label: "Home", icon: Home, testId: "nav-item-home" },
  { to: "/discover", label: "Discover", icon: Compass, testId: "nav-item-discover" },
  { to: "/random", label: "Random Chat", icon: Video, testId: "nav-item-random-chat" },
  { to: "/friends", label: "Friends", icon: Users, testId: "nav-item-friends" },
  { to: "/messages", label: "Messages", icon: MessageCircle, testId: "nav-item-messages", badgeKey: "messages" },
  { to: "/public-chat", label: "Public Chat", icon: Globe, testId: "nav-item-public-chat" },
  { to: "/profile", label: "Profile", icon: User, testId: "nav-item-profile" },
  { to: "/wallet", label: "Wallet & Store", icon: Coins, testId: "nav-item-wallet" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-item-settings" },
];

export function SidebarContent({ onNavigate, showLogo = true }) {
  const { logout, user } = useAuth();
  const { t } = useLanguage();
  const { subscribe } = useRealtime();
  const navigate = useNavigate();
  const location = useLocation();
  const [publicUnread, setPublicUnread] = useState(false);
  const [msgUnread, setMsgUnread] = useState(0);
  const nav = user?.role === "admin" || user?.role === "moderator" ? [...NAV, { to: "/admin", label: "Moderation", icon: Shield, testId: "nav-item-admin" }] : NAV;

  useEffect(() => subscribeMessageUnread(setMsgUnread), []);
  useEffect(() => { refreshMessageUnread(); }, []);

  useEffect(() => subscribe((evt) => {
    if (evt.type === "public:message" && evt.message?.sender_id !== user?.user_id && location.pathname !== "/public-chat") setPublicUnread(true);
  }), [subscribe, user?.user_id, location.pathname]);

  useEffect(() => { if (location.pathname === "/public-chat") setPublicUnread(false); }, [location.pathname]);

  useEffect(() => subscribe((evt) => {
    if (evt.type === "message" || evt.type === "read") refreshMessageUnread({ force: true });
  }), [subscribe]);

  useEffect(() => {
    const h = () => refreshMessageUnread({ force: true });
    window.addEventListener("yaprio:refresh-msg-unread", h);
    return () => window.removeEventListener("yaprio:refresh-msg-unread", h);
  }, []);

  const handleLogout = async () => { await logout(); toast("Signed out"); navigate("/", { replace: true }); };

  return <div className="flex h-full flex-col overflow-hidden p-5">
    <div>
      {showLogo && <div className="px-1 pb-4 pt-1"><YaprioLogo /></div>}
      <nav className="space-y-1 overflow-y-auto">
        {nav.map(({ to, label, icon: Icon, testId, soon, badgeKey }) => <NavLink key={to} to={to} onClick={onNavigate} data-testid={testId} className={({ isActive }) => `group flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition-all ${isActive ? "bg-pink-500 text-white shadow-lg shadow-pink-500/20" : "text-slate-600 hover:bg-pink-50 dark:text-slate-200 dark:hover:bg-white/5"}`}>
          <Icon className="h-5 w-5 shrink-0" /><span className="flex-1">{t(label)}</span>
          {publicUnread && label === "Public Chat" && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500 shadow-sm" data-testid="sidebar-public-chat-dot" />}
          {soon && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-orange-700">soon</span>}
          {badgeKey === "messages" && msgUnread > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-bold text-white" data-testid="sidebar-messages-badge">{msgUnread > 9 ? "9+" : msgUnread}</span>}
        </NavLink>)}
      </nav>
    </div>
    <div className="mt-4 space-y-3">
      <Button variant="ghost" onClick={handleLogout} data-testid="btn-logout" className="w-full justify-start gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-500 hover:bg-red-50 hover:text-red-500"><LogOut className="h-5 w-5" />{t("Sign out")}</Button>
    </div>
  </div>;
}

export default function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const open = () => setMobileOpen(true);
    window.addEventListener("yaprio:open-mobile-menu", open);
    return () => window.removeEventListener("yaprio:open-mobile-menu", open);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => { if (event.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = previous; window.removeEventListener("keydown", onKey); };
  }, [mobileOpen]);

  const username = user?.username || "Your profile";
  const initial = username.charAt(0).toUpperCase();

  return <>
    <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 border-r border-slate-100 bg-white dark:border-white/10 dark:bg-[#101116] lg:block"><SidebarContent /></aside>
    {mobileOpen && <div className="fixed inset-0 z-[200] lg:hidden" data-testid="mobile-navigation-overlay">
      <button type="button" aria-label="Close navigation menu" onClick={() => setMobileOpen(false)} className="absolute inset-0 h-full w-full bg-black/60 backdrop-blur-[2px]" />
      <aside role="dialog" aria-modal="true" aria-label="Navigation menu" data-testid="mobile-navigation-drawer" className="absolute inset-y-0 left-0 flex w-[min(86vw,340px)] flex-col border-r border-white/10 bg-white shadow-2xl animate-in slide-in-from-left duration-200 dark:bg-[#101116]">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-white/10">
          <YaprioLogo />
          <button type="button" onClick={() => setMobileOpen(false)} aria-label="Close menu" className="flex h-10 w-10 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
        <button type="button" onClick={() => { setMobileOpen(false); window.location.assign("/profile"); }} data-testid="mobile-menu-profile" className="mx-4 my-4 flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-3 text-left transition hover:border-pink-200 hover:bg-pink-50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10">
          <Avatar className="h-14 w-14 border-2 border-pink-400 ring-2 ring-pink-400/20">
            <AvatarImage src={mediaUrl(user?.avatar_url)} alt={username} />
            <AvatarFallback className="bg-pink-500 font-bold text-white">{initial}</AvatarFallback>
          </Avatar>
          <span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold text-slate-900 dark:text-white">@{username.replace(/^@/, "")}</span><span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">{t("View profile")}</span></span>
          <span className="text-xl text-slate-400">›</span>
        </button>
        <div className="min-h-0 flex-1 overflow-y-auto"><SidebarContent onNavigate={() => setMobileOpen(false)} showLogo={false} /></div>
      </aside>
    </div>}
  </>;
}
